import { off, on } from 'common/tools/dom/events';
import getMobileOS from 'common/tools/dom/getMobileOS';
import { navigateTo, replaceWith } from 'common/tools/dom/navigateTo';
import readAttribute from 'common/tools/dom/readAttribute';

// Delay before assuming the app is not installed and redirecting to the store.
export const FALLBACK_DELAY = 1500;
// Beyond this elapsed time the timer was most likely suspended while the app
// was in the foreground: the user is coming back, so we must not redirect.
const MAX_ELAPSED = 2000;
// Set on the wrapper once a mobile OS has been detected: the stylesheet then
// swaps the server-rendered desktop copy for the mobile one.
export const MOBILE_CLASS = 'app-redirect-mobile';

// Only one attempt can be in flight: two of them would stack timers and
// listeners, and end up redirecting to the store twice. Cancels the attempt
// still running, if any.
let cancelPending: (() => void) | null = null;

/**
 * Try to open the app through its deep link. If the page stays visible (the app
 * did not take focus), redirect to the store so the user can download the app.
 *
 * A new attempt — the user hitting the manual button — replaces the one still
 * running instead of being dropped.
 */
const openApp = (deepLink: string, storeUrl: string) => {
  cancelPending?.();

  const start = Date.now();
  let timer = 0;

  function cleanup() {
    cancelPending = null;
    window.clearTimeout(timer);
    off(document, 'visibilitychange', onHidden);
    off(window, 'pagehide', cleanup);
  }

  function onHidden() {
    // The app took over and the page went to the background: no fallback.
    if (document.visibilityState === 'hidden') {
      cleanup();
    }
  }

  cancelPending = cleanup;

  // Only a page that actually went away means the app took over. `blur` fires
  // for far less than that — on iOS the "cannot open the page" alert and the
  // "Open in ...?" confirmation both blur the window while leaving the user
  // right here, which is exactly when the store fallback has to happen.
  on(document, 'visibilitychange', onHidden);
  on(window, 'pagehide', cleanup);

  timer = window.setTimeout(() => {
    cleanup();

    if (
      document.visibilityState === 'visible' &&
      Date.now() - start < MAX_ELAPSED
    ) {
      // Replace, never push: this page redirects on load, so leaving it in the
      // history would send the user straight back to the store when they hit
      // back, with no way out to the newsletter.
      replaceWith(storeUrl);
    }
  }, FALLBACK_DELAY);

  navigateTo(deepLink);
};

/**
 * Rebuild the deep link from the brand prefix the server rendered and the
 * target carried by the URL fragment, which never reaches the server:
 * `#news?code=42` on an AlloCiné page gives `allocine://open/news?code=42`.
 * An empty fragment opens the app on its home screen.
 */
const readDeepLink = (prefix: string) =>
  prefix + window.location.hash.slice(1).replace(/^\/+/, '');

/**
 * Hand Chrome Android an intent URL rather than a bare scheme navigation.
 * Chrome drops a scheme navigation made without a user gesture often enough
 * that the timer would send a user who *has* the app to the Play Store. An
 * intent URL lets the OS decide instead: it opens the app when it is there and
 * follows `browser_fallback_url` when it is not, without the page guessing.
 */
const toIntentLink = (
  deepLink: string,
  androidPackage: string,
  storeUrl: string
) => {
  const separator = deepLink.indexOf('://');

  if (separator < 0) {
    return deepLink;
  }

  const scheme = deepLink.slice(0, separator);
  // Everything after the scheme becomes the intent data, where a `#` would
  // otherwise be read as the start of the intent block.
  const data = deepLink.slice(separator + 3).replace(/#/g, '%23');

  return (
    `intent://${data}#Intent;scheme=${scheme};package=${androidPackage};` +
    `S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`
  );
};

/**
 * Point the jan payload at the deep link. The server only knows the brand
 * prefix, the target living in the fragment, so every click would otherwise
 * report the same value and hide which content actually drives app opens.
 *
 * The deep link is reported on Android too rather than the intent URL: the
 * wrapper is how the OS is asked to open the app, not what the click is about.
 */
const trackDeepLink = (link: HTMLAnchorElement, deepLink: string) => {
  const tracking = readAttribute<Record<string, unknown>>(link, 'data-jan');

  // A payload the server did not render as an object is left alone.
  if (!tracking || typeof tracking !== 'object') {
    return;
  }

  link.dataset.jan = JSON.stringify({ ...tracking, filters_value: deepLink });
};

/**
 * Buffer page reached from the newsletters: on a mobile OS it opens the app
 * through its deep link as soon as the page loads, and falls back to the store
 * of that OS when nothing takes the page over. On desktop the server-rendered
 * page already shows the right copy and both store links, so nothing happens.
 */
export default function initAppRedirect() {
  const wrapper = document.querySelector<HTMLElement>('.js-app-redirect');

  if (!wrapper) {
    return;
  }

  const os = getMobileOS();

  if (os === 'other') {
    return;
  }

  const prefix = readAttribute<string>(wrapper, 'data-deeplink-prefix');
  const storeUrl = readAttribute<string>(
    wrapper,
    os === 'ios' ? 'data-ios-store-url' : 'data-android-store-url'
  );

  if (!prefix || !storeUrl) {
    return;
  }

  const deepLink = readDeepLink(prefix);
  const androidPackage = readAttribute<string>(wrapper, 'data-android-package');
  // The intent URL already carries its own fallback, but the timer stays: an
  // in-app WebView may ignore `intent://` altogether and leave the user here.
  const openUrl =
    os === 'android' && androidPackage
      ? toIntentLink(deepLink, androidPackage, storeUrl)
      : deepLink;

  wrapper.classList.add(MOBILE_CLASS);

  // Browsers do not always honour a scheme navigation made without a user
  // gesture, so the manual button stays the guaranteed way in.
  const link = wrapper.querySelector<HTMLAnchorElement>(
    '.js-app-redirect-open'
  );

  const attempt = () => openApp(openUrl, storeUrl);

  if (link) {
    // The server can only render the prefix, the target being in the fragment.
    link.href = openUrl;
    trackDeepLink(link, deepLink);

    on(link, 'click', (event: Event) => {
      event.preventDefault();

      attempt();
    });
  }

  attempt();
}
